/**
 * Shared pieces for the Load Data Pipeline pages.
 *
 * The dashboard's job is auditability, so these lean on showing the reader
 * what a number means and when it is missing, rather than rendering a
 * confident figure over absent data.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Typography from '@mui/joy/Typography'
import * as React from 'react'

/** Windows the server will honour. Anything larger is clamped server-side. */
const WINDOW_OPTIONS = [
    { label: 'Last hour', hours: 1 },
    { label: 'Last 6 hours', hours: 6 },
    { label: 'Last 24 hours', hours: 24 },
    { label: 'Last 48 hours', hours: 48 },
    { label: 'Last 7 days', hours: 168 },
]

export function WindowPicker({ hours, onChange, maxHours }: { hours: number; onChange: (h: number) => void; maxHours?: number }) {
    const options = maxHours ? WINDOW_OPTIONS.filter((o) => o.hours <= maxHours) : WINDOW_OPTIONS
    return (
        <Select size="sm" value={hours} onChange={(_, v) => v && onChange(v)} sx={{ minWidth: 160 }}>
            {options.map((o) => (
                <Option key={o.hours} value={o.hours}>
                    {o.label}
                </Option>
            ))}
        </Select>
    )
}

/**
 * A single headline number.
 *
 * `hint` exists because most of these figures are meaningless without knowing
 * what they count — "3,207 rows" means nothing until you know a row is one
 * (lane, equipment, hour, writer) cell.
 */
export function Stat({
    label,
    value,
    hint,
    tone,
}: {
    label: string
    value: React.ReactNode
    hint?: string
    tone?: 'neutral' | 'warning' | 'danger' | 'success'
}) {
    return (
        <Card variant="soft" color={tone ?? 'neutral'} sx={{ flex: 1, minWidth: 160 }}>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography level="h3">{value}</Typography>
            {hint ? (
                <Typography level="body-xs" textColor="text.tertiary">
                    {hint}
                </Typography>
            ) : null}
        </Card>
    )
}

export function StatRow({ children }: { children: React.ReactNode }) {
    return <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>{children}</Box>
}

export function Loading() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
        </Box>
    )
}

/**
 * Distinguishes "the dashboard cannot see the pipeline" from "the pipeline is
 * broken". A 503 here means main-service could not reach the Market Intel
 * database — the consumer may well still be writing to it. A page reading
 * something else (C-Search reads load-search-service) names it in `unreachable`.
 */
export function ErrorState({ error, unreachable }: { error: unknown; unreachable?: { title: string; body: string } }) {
    const status = (error as { response?: { status?: number } })?.response?.status
    const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error)?.message ?? 'Unknown error'

    if (status === 503) {
        if (unreachable) {
            return (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">{unreachable.title}</Typography>
                        <Typography level="body-sm">{unreachable.body}</Typography>
                        <Typography level="body-xs" textColor="text.tertiary">
                            {message}
                        </Typography>
                    </Box>
                </Alert>
            )
        }
        return (
            <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="title-sm">Market Intel database unreachable</Typography>
                    <Typography level="body-sm">
                        This dashboard cannot reach the database. That does not mean the pipeline stopped — the consumer writes over the VPC and may still be
                        running. Check the consumer&apos;s flush logs to tell the two apart.
                    </Typography>
                </Box>
            </Alert>
        )
    }

    return (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
            {message}
        </Alert>
    )
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatNumber(n: number): string {
    return n.toLocaleString()
}

/** Freshness, phrased so a stale pipeline reads as stale rather than as a timestamp. */
export function formatLag(seconds: number | null): { text: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } {
    if (seconds === null) return { text: 'no data', tone: 'neutral' }
    if (seconds < 3600) return { text: `${Math.round(seconds / 60)}m ago`, tone: 'success' }
    if (seconds < 6 * 3600) return { text: `${Math.round(seconds / 3600)}h ago`, tone: 'warning' }
    return { text: `${Math.round(seconds / 3600)}h ago`, tone: 'danger' }
}

/**
 * One fetch/loading/error/cancel lifecycle for every page in this section.
 *
 * This was copy-pasted into four pages before it lived here — identical
 * `cancelled` flag, identical then/catch/finally. A fix to the cancellation or
 * error path would have needed four coordinated edits, and the fourth is the
 * one that gets missed.
 */
export function useLoadPipelineData<T>(fetcher: () => Promise<T>, deps: React.DependencyList): { data: T | null; error: unknown; loading: boolean } {
    const [data, setData] = React.useState<T | null>(null)
    const [error, setError] = React.useState<unknown>(null)
    const [loading, setLoading] = React.useState(true)

    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError(null)
        fetcher()
            .then((res) => {
                if (!cancelled) setData(res)
            })
            .catch((err) => {
                if (!cancelled) setError(err)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // The fetcher closes over the page's own params; `deps` is what those
        // pages already keyed their effect on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)

    return { data, error, loading }
}
